import React, { useState } from 'react';
import { Persona, PersonaCreate, PersonaUpdate } from '../../types/persona';
import { PersonaCard } from './PersonaCard';
import { useTheme } from '../../contexts/ThemeContext';
import { normalizePersonaName, finalizePersonaName, validatePersonaName } from '../../utils/validationUtils';

interface PersonaCustomizerProps {
  initialPersona?: Persona;
  onSave: (persona: PersonaCreate | PersonaUpdate) => void;
  onCancel: () => void;
  isSaving?: boolean;
  error?: any;
}

export const PersonaCustomizer: React.FC<PersonaCustomizerProps> = ({
  initialPersona,
  onSave,
  onCancel,
  isSaving = false,
  error
}) => {
  const isEditMode = !!initialPersona;
  const { darkMode } = useTheme();
  
  const [persona, setPersona] = useState<PersonaCreate | PersonaUpdate>(
    initialPersona || {
      name: '',
      shortDescription: '',
      fullDescription: '',
      tags: [],
      primarySpecialization: '',
      capabilities: {
        creativity: 5,
        technical: 5,
        analytical: 5,
        communication: 5
      },
      allowedTools: [],
      prompt: ''
    }
  );
  
  const [step, setStep] = useState(1);
  const [previewMode, setPreviewMode] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newTool, setNewTool] = useState('');
  
  const updatePersona = (updates: Partial<PersonaCreate>) => {
    setPersona(prev => ({
      ...prev,
      ...updates
    }));
  };
  
  const updateCapability = (name: string, value: number) => {
    setPersona(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [name]: value
      }
    }));
  };
  
  const addTag = () => {
    if (newTag.trim() && !persona.tags.includes(newTag.trim())) {
      setPersona(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };
  
  const removeTag = (tag: string) => {
    setPersona(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  const addTool = () => {
    if (newTool.trim() && !persona.allowedTools.includes(newTool.trim())) {
      setPersona(prev => ({
        ...prev,
        allowedTools: [...prev.allowedTools, newTool.trim()]
      }));
      setNewTool('');
    }
  };
  
  const removeTool = (tool: string) => {
    setPersona(prev => ({
      ...prev,
      allowedTools: prev.allowedTools.filter(t => t !== tool)
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(persona);
  };
  
  const [isComposing, setIsComposing] = useState(false);

  // Soft-warning branch: when name is valid but recommends uppercase start,
  // nameValidation.valid is true AND nameValidation.message is set. The JSX
  // below renders the message in gray (not red) for that case. Intentional.
  const nameValidation = persona.name.length > 0
    ? validatePersonaName(persona.name)
    : { valid: true, message: undefined as string | undefined };

  const isFormValid = () => {
    return (
      persona.name.trim() !== '' &&
      validatePersonaName(persona.name).valid &&
      persona.shortDescription.trim() !== '' &&
      persona.primarySpecialization.trim() !== '' &&
      persona.prompt.trim() !== ''
    );
  };
  
  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md overflow-hidden`}>
      <div className={`p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : ''} flex justify-between items-center`}>
        <h2 className="text-xl font-semibold">
          {isEditMode ? 'Edit Persona' : 'Create New Persona'}
        </h2>
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Preview</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              previewMode ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            onClick={() => setPreviewMode(!previewMode)}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                previewMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
      
      <div className={`grid ${previewMode ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
        <div className="p-6">
          <div className="mb-4">
            <div className="flex space-x-2 mb-6">
              {[1, 2, 3, 4, 5].map(stepNum => (
                <button
                  key={stepNum}
                  type="button"
                  className={`flex-1 py-2 ${
                    step === stepNum
                      ? 'bg-blue-600 text-white'
                      : step > stepNum
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  } rounded`}
                  onClick={() => setStep(stepNum)}
                >
                  Step {stepNum}
                </button>
              ))}
            </div>
            
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Basic Information</h3>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      } ${
                        persona.name.length > 0 && !nameValidation.valid
                          ? darkMode ? 'border border-red-500' : 'border-2 border-red-500'
                          : ''
                      }`}
                      value={persona.name}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = isComposing ? raw : normalizePersonaName(raw);
                        updatePersona({ name: next });
                      }}
                      onCompositionStart={() => setIsComposing(true)}
                      onCompositionEnd={(e) => {
                        setIsComposing(false);
                        updatePersona({ name: finalizePersonaName(e.currentTarget.value) });
                      }}
                      onBlur={(e) => {
                        // Finalize on blur — strip edge underscores the live
                        // transform preserved for left-to-right typing.
                        updatePersona({ name: finalizePersonaName(e.currentTarget.value) });
                      }}
                      autoCapitalize="off"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      required
                    />
                    {persona.name.length > 0 && nameValidation.message && (
                      <p className={`text-xs mt-1 ${!nameValidation.valid ? 'text-red-500' : 'text-gray-500'}`}>
                        {nameValidation.message}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Use letters, numbers, and underscores (up to 80). Spaces and other characters become <code>_</code> as you type. Latin alphabet only.
                    </p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Short Description <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={persona.shortDescription}
                      onChange={(e) => updatePersona({ shortDescription: e.target.value })}
                      required
                    />
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      A brief one-line description of this persona
                    </p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Full Description
                    </label>
                    <textarea
                      className={`w-full px-3 py-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      rows={4}
                      value={persona.fullDescription}
                      onChange={(e) => updatePersona({ fullDescription: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Primary Specialization <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={persona.primarySpecialization}
                      onChange={(e) => updatePersona({ primarySpecialization: e.target.value })}
                      required
                    />
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      The main area of expertise (e.g., Technical, Creative, Analytical)
                    </p>
                  </div>
                </div>
              )}
              
              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Tags & Categories</h3>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Tags
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        className={`flex-grow px-3 py-2 border rounded-l-md ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Add a tag"
                      />
                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                        onClick={addTag}
                      >
                        Add
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-2">
                      {persona.tags.map((tag, index) => (
                        <span
                          key={`tag-${index}-${tag}`}
                          className={`px-3 py-1 text-sm rounded-full flex items-center ${
                            darkMode 
                              ? 'bg-gray-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {tag}
                          <button
                            type="button"
                            className={`ml-2 ${
                              darkMode 
                                ? 'text-gray-400 hover:text-gray-200' 
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => removeTag(tag)}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      {persona.tags.length === 0 && (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No tags added yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Capabilities</h3>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Creativity
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        className="flex-grow"
                        value={persona.capabilities.creativity}
                        onChange={(e) => updateCapability('creativity', parseInt(e.target.value))}
                      />
                      <span className="ml-2 w-8 text-center">{persona.capabilities.creativity}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Technical
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        className="flex-grow"
                        value={persona.capabilities.technical}
                        onChange={(e) => updateCapability('technical', parseInt(e.target.value))}
                      />
                      <span className="ml-2 w-8 text-center">{persona.capabilities.technical}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Analytical
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        className="flex-grow"
                        value={persona.capabilities.analytical}
                        onChange={(e) => updateCapability('analytical', parseInt(e.target.value))}
                      />
                      <span className="ml-2 w-8 text-center">{persona.capabilities.analytical}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Communication
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        className="flex-grow"
                        value={persona.capabilities.communication}
                        onChange={(e) => updateCapability('communication', parseInt(e.target.value))}
                      />
                      <span className="ml-2 w-8 text-center">{persona.capabilities.communication}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {step === 4 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Tools & Access</h3>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Allowed Tools
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        className={`flex-grow px-3 py-2 border rounded-l-md ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        value={newTool}
                        onChange={(e) => setNewTool(e.target.value)}
                        placeholder="Add a tool"
                      />
                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                        onClick={addTool}
                      >
                        Add
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-2">
                      {persona.allowedTools.map((tool, index) => (
                        <span
                          key={`tool-${index}-${tool}`}
                          className={`px-3 py-1 text-sm rounded-full flex items-center ${
                            darkMode 
                              ? 'bg-green-900 bg-opacity-50 text-green-300' 
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {tool}
                          <button
                            type="button"
                            className={`ml-2 ${
                              darkMode 
                                ? 'text-green-400 hover:text-green-200' 
                                : 'text-green-500 hover:text-green-700'
                            }`}
                            onClick={() => removeTool(tool)}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      {persona.allowedTools.length === 0 && (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No tools added yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {step === 5 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">System Prompt</h3>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Prompt <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={`w-full px-3 py-2 border rounded-md font-mono text-sm ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      rows={12}
                      value={persona.prompt}
                      onChange={(e) => updatePersona({ prompt: e.target.value })}
                      required
                    />
                    <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      The system prompt that defines this persona's behavior and capabilities
                    </p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Avatar URL
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      value={persona.avatarUrl || ''}
                      onChange={(e) => updatePersona({ avatarUrl: e.target.value })}
                      placeholder="https://example.com/avatar.png"
                    />
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-between">
                <div>
                  {step > 1 && (
                    <button
                      type="button"
                      className={`px-4 py-2 border rounded-md ${
                        darkMode 
                          ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setStep(step - 1)}
                    >
                      Previous
                    </button>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    className={`px-4 py-2 border rounded-md ${
                      darkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                  
                  {step < 5 ? (
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      onClick={() => setStep(step + 1)}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                      disabled={!isFormValid() || isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Persona'}
                    </button>
                  )}
                </div>
              </div>
              
              {error && (
                <div className={`mt-4 p-3 rounded-md ${
                  darkMode 
                    ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {error.message || 'An error occurred while saving the persona'}
                </div>
              )}
            </form>
          </div>
        </div>
        
        {previewMode && (
          <div className={`p-6 ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50'} border-l`}>
            <h3 className="text-lg font-medium mb-4">Preview</h3>
            <PersonaCard
              persona={{
                ...persona,
                id: initialPersona?.id || 'preview',
                created_at: initialPersona?.created_at || new Date().toISOString(),
                updated_at: initialPersona?.updated_at || new Date().toISOString()
              }}
            />
            
            <div className="mt-6">
              <h4 className="font-medium mb-2">System Prompt Preview</h4>
              <div className={`p-4 rounded-md border ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <pre className={`whitespace-pre-wrap text-sm font-mono ${darkMode ? 'text-gray-300' : ''}`}>{persona.prompt}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
